import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import IconButton from 'components/partials/button/icon-button';
import { useModalFocusTrap } from 'components/partials/modal/use-modal-focus-trap';
import { useSideMenuContext } from 'components/shared/side-menu/side-menu-layout';
import { SIDEMENU_WIDTH } from 'components/shared/side-menu/side-menu.constants';

import Icons from 'assets/icons';

const EXIT_DURATION_MS = 200;
const EXIT_FALLBACK_MS = EXIT_DURATION_MS + 100;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  title: React.ReactNode;
  headerSlot?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  width?: string;
  'data-testid'?: string;
  closeAriaLabel?: string;
}

interface DrawerPanelProps extends Omit<DrawerProps, 'open' | 'onExited'> {
  panelRef: React.RefObject<HTMLDivElement | null>;
  isVisible: boolean;
  isClosing: boolean;
  onTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void;
}

const DrawerPanel = ({
  panelRef,
  isVisible,
  isClosing,
  onTransitionEnd,
  onClose,
  title,
  headerSlot,
  footer,
  children,
  width = 'w-screen tablet:w-[480px]',
  'data-testid': dataTestId = 'drawer',
  closeAriaLabel = 'Close drawer',
}: DrawerPanelProps): React.JSX.Element => {
  const titleId = useId();
  const { isCollapsed } = useSideMenuContext();

  useModalFocusTrap(panelRef);

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;

    panelRef.current?.focus({ preventScroll: true });

    return () => {
      trigger?.focus?.();
    };
  }, [panelRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.stopPropagation();
      if (!isClosing) onClose();
    }
  };

  const handleClose = (): void => {
    if (!isClosing) onClose();
  };

  return (
    <div
      className={classNames('fixed inset-0 z-[701] flex justify-end', {
        'pointer-events-none': isClosing,
      })}
      data-testid={`${dataTestId}-wrapper`}
    >
      <div
        role="presentation"
        className={classNames(
          'absolute inset-y-0 right-0 bg-navy-950/20 transition-opacity motion-reduce:transition-none',
          isVisible
            ? 'opacity-100 duration-300 ease-out'
            : 'opacity-0 duration-200 ease-in',
          isCollapsed
            ? SIDEMENU_WIDTH.collapsedLeft
            : SIDEMENU_WIDTH.expandedLeft
        )}
        onClick={handleClose}
        data-testid={`${dataTestId}-backdrop`}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- APG dialog Escape handling bubbles from focusable descendants; react-select must see Escape first */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onTransitionEnd={onTransitionEnd}
        className={twMerge(
          'relative flex h-full max-w-full flex-col border-l border-navy-200 bg-white text-gray-700 transition-transform focus:outline-none motion-reduce:transition-none',
          isVisible
            ? 'translate-x-0 duration-300 ease-out'
            : 'translate-x-full duration-200 ease-in',
          width
        )}
        data-testid={dataTestId}
      >
        <div className="flex flex-col gap-1 border-b border-navy-100 pb-3 pl-5 pr-10 pt-5">
          {headerSlot}
          <h2 id={titleId} className="text-lg font-bold text-navy-800">
            {title}
          </h2>
          <IconButton
            variant="reserve-text"
            size="sm"
            icon={<Icons.Close aria-hidden="true" />}
            aria-label={closeAriaLabel}
            onClick={handleClose}
            className="absolute right-2 top-2"
            data-testid={`${dataTestId}-close`}
          />
        </div>
        <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-3">
          {children}
        </div>
        {footer && (
          <div className="flex gap-2 border-t border-navy-100 p-5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

const Drawer = ({
  open,
  onExited,
  ...panelProps
}: DrawerProps): React.ReactPortal | null => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(open);
  const [hasEntered, setHasEntered] = useState(false);
  const wasMounted = useRef(isMounted);

  const finishExit = (): void => {
    setIsMounted(false);
    setHasEntered(false);
  };

  if (open && !isMounted) setIsMounted(true);
  if (!open && isMounted && prefersReducedMotion()) finishExit();

  useLayoutEffect(() => {
    if (!open || !isMounted) return;

    panelRef.current?.getBoundingClientRect();
    setHasEntered(true);
  }, [open, isMounted]);

  useEffect(() => {
    if (open || !isMounted) return undefined;

    const timer = setTimeout(finishExit, EXIT_FALLBACK_MS);

    return () => clearTimeout(timer);
  }, [open, isMounted]);

  useEffect(() => {
    if (wasMounted.current && !isMounted) onExited?.();
    wasMounted.current = isMounted;
  }, [isMounted, onExited]);

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>
  ): void => {
    if (
      !open &&
      event.target === event.currentTarget &&
      event.propertyName === 'transform'
    ) {
      finishExit();
    }
  };

  if (!isMounted) return null;

  return createPortal(
    <DrawerPanel
      {...panelProps}
      panelRef={panelRef}
      isVisible={open && hasEntered}
      isClosing={!open}
      onTransitionEnd={handleTransitionEnd}
    />,
    document.body
  );
};

export default Drawer;
